import { memo } from 'react';

/**
 * Props for the ContentLoader component
 */
export interface ContentLoaderProps {
  /** Whether the loader is visible */
  isVisible: boolean;
  /** Loading message to display */
  message?: string;
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className for additional styling */
  className?: string;
}

/**
 * ContentLoader component
 * Provides loading state for main content area only (not full page)
 */
function ContentLoaderComponent({
  isVisible,
  message = 'Loading...',
  size = 'md',
  className = '',
}: ContentLoaderProps) {
  if (!isVisible) return null;

  const spinnerSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  const textSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div
      className={`flex flex-col items-center justify-center py-12 px-4 ${className}`}
      role="loading"
      aria-label={message}
    >
      {/* Modern Spinner */}
      <div className="relative mb-4">
        <div
          className={`${spinnerSizes[size]} border-2 border-gray-200 rounded-full`}
        />
        <div
          className={`absolute top-0 left-0 ${spinnerSizes[size]} border-2 border-transparent border-t-blue-600 rounded-full animate-spin`}
          style={{ animationDuration: '0.8s' }}
        />
      </div>

      {/* Loading Message */}
      <div className="text-center">
        <p className={`font-medium text-gray-700 ${textSizes[size]}`}>
          {message}
        </p>
        <div className="flex items-center justify-center space-x-1 mt-2">
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
        </div>
      </div>
    </div>
  );
}

/**
 * Memoized ContentLoader component for optimal performance
 */
export const ContentLoader = memo(ContentLoaderComponent) as typeof ContentLoaderComponent;